document.documentElement.classList.add('js');

const AyuTheme = {
  lockScroll(lock = true) {
    document.documentElement.classList.toggle('is-locked', lock);
  },

  formatMoney(cents) {
    const currency = document.body?.dataset.currency || 'USD';
    const locale = document.documentElement.lang || 'en-US';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format((cents || 0) / 100);
  },

  emitAnalytics(eventName, detail = {}) {
    const payload = {
      event: eventName,
      page_type: document.body?.dataset.pageType || '',
      timestamp: Date.now(),
      ...detail,
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(
      new CustomEvent('ayu:analytics', {
        detail: payload,
      })
    );

    return payload;
  },

  getCartCount() {
    const firstNode = document.querySelector('[data-cart-count]');
    if (!firstNode) return 0;
    const parsed = Number(firstNode.textContent || 0);
    return Number.isFinite(parsed) ? parsed : 0;
  },

  setCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = count;
      node.hidden = count === 0;
    });
  },

  incrementCartCount(delta = 1) {
    this.setCartCount(Math.max(0, this.getCartCount() + delta));
  },

  refreshCartCount() {
    return fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => response.json())
      .then((cart) => {
        this.setCartCount(cart.item_count || 0);
        return cart;
      })
      .catch(() => null);
  },

  openDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    this.lockScroll(true);
  },

  closeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.drawer.is-open')) {
      this.lockScroll(false);
    }
  },
};

window.AyuTheme = AyuTheme;

document.addEventListener('click', (event) => {
  const openTrigger = event.target.closest('[data-drawer-open]');
  if (openTrigger) {
    const selector = openTrigger.getAttribute('data-drawer-open');
    AyuTheme.openDrawer(document.querySelector(selector));
  }

  const closeTrigger = event.target.closest('[data-drawer-close], .drawer__scrim');
  if (closeTrigger) {
    AyuTheme.closeDrawer(closeTrigger.closest('.drawer'));
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    const activeDrawer = document.querySelector('.drawer.is-open');
    if (activeDrawer) AyuTheme.closeDrawer(activeDrawer);
  }
});

document.addEventListener('change', (event) => {
  const sortInput = event.target.closest('[data-auto-submit]');
  if (sortInput) {
    AyuTheme.emitAnalytics('sort_change', {
      sort_by: sortInput.value,
      context: sortInput.dataset.sortContext || document.body?.dataset.pageType || 'collection',
    });
    sortInput.form?.submit();
  }
});

document.addEventListener('submit', (event) => {
  const searchForm = event.target.closest('[data-search-form]');
  if (!searchForm) return;

  const query = searchForm.querySelector('input[type="search"]')?.value?.trim() || '';
  AyuTheme.emitAnalytics('search', { query });
});

document.addEventListener('click', (event) => {
  const analyticsTarget = event.target.closest('[data-analytics-click]');
  if (!analyticsTarget) return;

  AyuTheme.emitAnalytics(analyticsTarget.dataset.analyticsClick, {
    item_id: analyticsTarget.dataset.itemId || '',
    item_name: analyticsTarget.dataset.itemName || '',
    item_type: analyticsTarget.dataset.itemType || '',
    list_context: analyticsTarget.dataset.listContext || '',
  });
});

document.addEventListener('click', (event) => {
  const tabButton = event.target.closest('[data-tab-button]');
  if (!tabButton) return;

  const tabRoot = tabButton.closest('[data-collection-tabs]');
  if (!tabRoot) return;

  const tabId = tabButton.dataset.tabId;
  if (!tabId) return;

  tabRoot.querySelectorAll('[data-tab-button]').forEach((button) => {
    const isActive = button === tabButton;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });

  tabRoot.querySelectorAll('[data-tab-panel]').forEach((panel) => {
    panel.hidden = panel.dataset.tabPanel !== tabId;
  });

  AyuTheme.emitAnalytics('collection_tab_change', {
    tab_id: tabId,
    tab_label: tabButton.textContent.trim(),
  });
});

class ProductCardController {
  constructor(root) {
    this.root = root;
    this.productId = root.dataset.productId || '';
    this.productTitle = root.dataset.productTitle || '';
    this.productUrl = root.dataset.productUrl || '';
    this.listContext = root.dataset.listContext || document.body?.dataset.pageType || '';
    this.addButtonLabel = root.dataset.addLabel || 'Add to bag';
    this.buyButtonLabel = root.dataset.buyLabel || 'Buy Now';
    this.soldOutLabel = root.dataset.soldOutLabel || 'Sold Out';
    this.addedSuccessLabel = root.dataset.addedSuccessLabel || 'Added to cart';
    this.addingLabel = root.dataset.addingLabel || 'Adding…';
    this.processingLabel = root.dataset.processingLabel || 'Processing…';
    this.variants = JSON.parse(root.querySelector('[data-product-card-variants]')?.textContent || '[]');
    this.selectedOptions = JSON.parse(
      root.querySelector('[data-product-card-selected-options]')?.textContent || '[]'
    );
    this.optionSlots = [...root.querySelectorAll('[data-product-card-option-slot]:not(.is-empty)')];
    this.optionButtons = [...root.querySelectorAll('[data-product-card-option-button]')];
    this.optionSelects = [...root.querySelectorAll('[data-product-card-option-select]')];
    this.variantInput = root.querySelector('[data-product-card-variant-input]');
    this.priceCurrent = root.querySelector('[data-product-card-price-current]');
    this.priceCompare = root.querySelector('[data-product-card-price-compare]');
    this.saleBadge = root.querySelector('[data-product-card-sale-badge]');
    this.mediaImage = root.querySelector('[data-product-card-image]');
    this.defaultImageSrc = this.mediaImage?.currentSrc || this.mediaImage?.src || '';
    this.defaultImageAlt = this.mediaImage?.alt || this.productTitle;
    this.linkTargets = [...root.querySelectorAll('[data-product-card-link]')];
    this.form = root.querySelector('[data-product-card-form]');
    this.addButton = root.querySelector('[data-product-card-add]');
    this.addLabel = root.querySelector('[data-product-card-add-label]');
    this.buyButton = root.querySelector('[data-product-card-buy]');
    this.buyLabel = root.querySelector('[data-product-card-buy-label]');
    this.message = root.querySelector('[data-product-card-message]');
    this.isBusy = false;
    this.hasSelectors = this.optionSlots.length > 0;

    this.handleResize = this.handleResize.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleRootClick = this.handleRootClick.bind(this);

    this.attachEvents();
    this.syncState(true);
    if (this.hasSelectors) {
      window.addEventListener('resize', this.handleResize);
    }
  }

  attachEvents() {
    this.optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled || this.isBusy) return;
        this.selectOption(Number(button.dataset.optionIndex), button.dataset.optionValue || '');
      });
    });

    this.optionSelects.forEach((select) => {
      select.addEventListener('change', () => {
        if (this.isBusy) return;
        this.selectOption(Number(select.dataset.optionIndex), select.value);
      });
    });

    this.form?.addEventListener('submit', this.handleSubmit);
    this.buyButton?.addEventListener('click', () => {
      if (this.isBusy) return;
      this.addToCart({ redirectToCheckout: true });
    });
    this.root.addEventListener('click', this.handleRootClick);
  }

  destroy() {
    if (this.hasSelectors) {
      window.removeEventListener('resize', this.handleResize);
    }
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.root.removeEventListener('click', this.handleRootClick);
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.isBusy) return;
    this.addToCart();
  }

  handleRootClick(event) {
    if (event.defaultPrevented) return;
    if (event.target.closest('a, button, select, option, input, label, form')) return;

    const variant = this.getCurrentVariant();
    const href = variant?.id ? `${this.productUrl}?variant=${variant.id}` : this.productUrl;
    if (href) {
      window.location.assign(href);
    }
  }

  handleResize() {
    this.updateSelectorModes();
  }

  getCurrentVariant() {
    return this.variants.find((variant) =>
      variant.options.every((value, index) => value === this.selectedOptions[index])
    );
  }

  getAvailableValues(optionIndex, selections = this.selectedOptions) {
    const exactMatches = this.variants.filter((variant) =>
      variant.options.every((value, index) => {
        if (index === optionIndex) return true;
        const selectedValue = selections[index];
        if (!selectedValue) return true;
        return value === selectedValue;
      })
    );

    return [...new Set(exactMatches.map((variant) => variant.options[optionIndex]).filter(Boolean))];
  }

  normalizeSelections() {
    for (let optionIndex = 0; optionIndex < this.optionSlots.length; optionIndex += 1) {
      const availableValues = this.getAvailableValues(optionIndex, this.selectedOptions);
      if (!availableValues.length) continue;

      if (!this.selectedOptions[optionIndex] || !availableValues.includes(this.selectedOptions[optionIndex])) {
        this.selectedOptions[optionIndex] = availableValues[0];
      }
    }
  }

  selectOption(optionIndex, optionValue) {
    this.selectedOptions[optionIndex] = optionValue;
    this.normalizeSelections();
    this.syncState();

    AyuTheme.emitAnalytics('variant_change', {
      product_id: this.productId,
      product_name: this.productTitle,
      option_index: optionIndex,
      option_value: optionValue,
      list_context: this.listContext,
    });
  }

  updateSelectorModes() {
    this.optionSlots.forEach((slot) => {
      const buttonsWrap = slot.querySelector('[data-product-card-option-buttons]');
      const selectWrap = slot.querySelector('[data-product-card-option-select-wrap]');
      if (!buttonsWrap || !selectWrap) return;

      const preferredMode = slot.dataset.preferredMode || 'buttons';
      let useSelect = preferredMode === 'select';
      if (!useSelect && buttonsWrap.scrollWidth > buttonsWrap.clientWidth + 4) {
        useSelect = true;
      }

      slot.classList.toggle('is-select-mode', useSelect);
      buttonsWrap.hidden = useSelect;
      selectWrap.hidden = !useSelect;
    });
  }

  syncState(initial = false) {
    this.normalizeSelections();
    const availableValuesByIndex = this.optionSlots.map((_, optionIndex) =>
      this.getAvailableValues(optionIndex, this.selectedOptions)
    );

    this.optionButtons.forEach((button) => {
      const optionIndex = Number(button.dataset.optionIndex);
      const optionValue = button.dataset.optionValue;
      const isSelected = this.selectedOptions[optionIndex] === optionValue;
      const isAvailable = availableValuesByIndex[optionIndex]?.includes(optionValue);

      button.classList.toggle('is-selected', isSelected);
      button.disabled = !isAvailable;
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.optionSelects.forEach((select) => {
      const optionIndex = Number(select.dataset.optionIndex);
      [...select.options].forEach((option) => {
        const isAvailable = availableValuesByIndex[optionIndex]?.includes(option.value);
        option.disabled = !isAvailable;
      });
      select.value = this.selectedOptions[optionIndex] || select.value;
    });

    this.updateSelectorModes();

    const variant = this.getCurrentVariant();
    if (variant && this.variantInput) {
      this.variantInput.value = variant.id;
    }

    this.updatePricing(variant);
    this.updateMedia(variant);
    this.updateLinks(variant);
    this.updateActions(variant);

    if (!initial) {
      this.hideMessage();
    }
  }

  updatePricing(variant) {
    if (!variant) return;

    if (this.priceCurrent) {
      this.priceCurrent.textContent = AyuTheme.formatMoney(variant.price);
    }

    if (this.priceCompare) {
      const hasComparePrice = Number(variant.compare_at_price) > Number(variant.price);
      this.priceCompare.hidden = !hasComparePrice;
      if (hasComparePrice) {
        this.priceCompare.textContent = AyuTheme.formatMoney(variant.compare_at_price);
      }
    }

    if (this.saleBadge) {
      const hasDiscount = Number(variant.compare_at_price) > Number(variant.price);
      this.saleBadge.hidden = !hasDiscount;
      if (hasDiscount) {
        const discount = Math.round(
          ((Number(variant.compare_at_price) - Number(variant.price)) / Number(variant.compare_at_price)) * 100
        );
        this.saleBadge.textContent = `${discount}% Off`;
      }
    }
  }

  updateMedia(variant) {
    if (!this.mediaImage) return;

    if (variant?.featured_image) {
      this.mediaImage.src = variant.featured_image;
      this.mediaImage.alt = variant.featured_image_alt || this.productTitle;
      this.mediaImage.removeAttribute('srcset');
      return;
    }

    if (this.defaultImageSrc) {
      this.mediaImage.src = this.defaultImageSrc;
      this.mediaImage.alt = this.defaultImageAlt;
      this.mediaImage.removeAttribute('srcset');
    }
  }

  updateLinks(variant) {
    const href = variant?.id ? `${this.productUrl}?variant=${variant.id}` : this.productUrl;
    this.linkTargets.forEach((link) => {
      link.setAttribute('href', href);
    });
  }

  updateActions(variant) {
    const isAvailable = Boolean(variant?.available);
    const addText = this.isBusy ? this.busyAddText : isAvailable ? this.addButtonLabel : this.soldOutLabel;
    const buyText = this.isBusy ? this.busyBuyText : isAvailable ? this.buyButtonLabel : this.soldOutLabel;

    if (this.addLabel) this.addLabel.textContent = addText;
    if (this.buyLabel) this.buyLabel.textContent = buyText;
    if (this.addButton) this.addButton.disabled = !isAvailable || this.isBusy;
    if (this.buyButton) this.buyButton.disabled = !isAvailable || this.isBusy;
  }

  setBusy(isBusy, addText, buyText) {
    this.isBusy = isBusy;
    this.busyAddText = addText || this.addingLabel;
    this.busyBuyText = buyText || this.processingLabel;
    this.updateActions(this.getCurrentVariant());
  }

  showMessage(text, tone = 'success') {
    if (!this.message) return;
    this.message.hidden = !text;
    this.message.textContent = text || '';
    this.message.dataset.tone = tone;
  }

  hideMessage() {
    if (!this.message) return;
    this.message.hidden = true;
    this.message.textContent = '';
    delete this.message.dataset.tone;
  }

  updateCartCount(count) {
    AyuTheme.setCartCount(count);
  }

  async addToCart({ redirectToCheckout = false } = {}) {
    const variant = this.getCurrentVariant();
    if (!variant?.id || !variant.available) return;

    this.setBusy(true, redirectToCheckout ? 'Adding…' : 'Adding…', redirectToCheckout ? 'Redirecting…' : 'Buy Now');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1 }],
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.description || 'Unable to add this product right now.');
      }

      AyuTheme.incrementCartCount(1);
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => AyuTheme.refreshCartCount());
      } else {
        window.setTimeout(() => AyuTheme.refreshCartCount(), 250);
      }

      AyuTheme.emitAnalytics('add_to_cart', {
        product_id: this.productId,
        product_name: this.productTitle,
        variant_id: variant.id,
        list_context: this.listContext,
        source: redirectToCheckout ? 'product_card_buy_now' : 'product_card_add_to_cart',
      });

      if (redirectToCheckout) {
        AyuTheme.emitAnalytics('begin_checkout', {
          product_id: this.productId,
          product_name: this.productTitle,
          variant_id: variant.id,
          list_context: this.listContext,
          source: 'product_card_buy_now',
        });
        window.location.assign('/checkout');
        return;
      }

      this.showMessage(this.addedSuccessLabel, 'success');
    } catch (error) {
      this.showMessage(error.message || 'Unable to add this product right now.', 'error');
    } finally {
      this.setBusy(false);
    }
  }
}

const productCardControllers = new WeakMap();

function initProductCards(container = document) {
  container.querySelectorAll?.('[data-product-card-root]').forEach((root) => {
    if (productCardControllers.has(root)) return;
    productCardControllers.set(root, new ProductCardController(root));
  });
}

function destroyProductCards(container = document) {
  container.querySelectorAll?.('[data-product-card-root]').forEach((root) => {
    const controller = productCardControllers.get(root);
    if (!controller) return;
    controller.destroy();
    productCardControllers.delete(root);
  });
}

AyuTheme.initProductCards = initProductCards;
AyuTheme.destroyProductCards = destroyProductCards;

document.addEventListener('DOMContentLoaded', () => {
  initProductCards(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initProductCards(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyProductCards(event.target);
});

class SlideshowController {
  constructor(root) {
    this.root = root;
    this.slides = [...root.querySelectorAll('[data-slideshow-slide]')];
    this.dots = [...root.querySelectorAll('[data-slideshow-dot]')];
    this.prevButton = root.querySelector('[data-slideshow-prev]');
    this.nextButton = root.querySelector('[data-slideshow-next]');
    this.autoplayEnabled = root.dataset.autoplay === 'true';
    this.interval = Number(root.dataset.interval || 5000);
    this.activeIndex = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('is-active'))
    );
    this.timer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;

    this.handlePrev = this.handlePrev.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);

    this.attachEvents();
    this.goTo(this.activeIndex, false);
    this.start();
  }

  attachEvents() {
    this.prevButton?.addEventListener('click', this.handlePrev);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
      });
    });
    this.root.addEventListener('mouseenter', this.handleMouseEnter);
    this.root.addEventListener('mouseleave', this.handleMouseLeave);
    this.root.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.root.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  destroy() {
    this.stop();
    this.prevButton?.removeEventListener('click', this.handlePrev);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.root.removeEventListener('mouseenter', this.handleMouseEnter);
    this.root.removeEventListener('mouseleave', this.handleMouseLeave);
    this.root.removeEventListener('touchstart', this.handleTouchStart);
    this.root.removeEventListener('touchend', this.handleTouchEnd);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handlePrev() {
    this.goTo(this.activeIndex - 1);
  }

  handleNext() {
    this.goTo(this.activeIndex + 1);
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stop();
    } else {
      this.start();
    }
  }

  handleMouseEnter() {
    this.stop();
  }

  handleMouseLeave() {
    this.start();
  }

  handleTouchStart(event) {
    this.touchStartX = event.changedTouches?.[0]?.clientX || 0;
  }

  handleTouchEnd(event) {
    this.touchEndX = event.changedTouches?.[0]?.clientX || 0;
    const delta = this.touchEndX - this.touchStartX;
    if (Math.abs(delta) < 40) return;
    if (delta > 0) {
      this.handlePrev();
    } else {
      this.handleNext();
    }
  }

  start() {
    if (!this.autoplayEnabled || this.slides.length < 2) return;
    this.stop();
    this.timer = window.setInterval(() => {
      this.goTo(this.activeIndex + 1, false);
    }, this.interval);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  goTo(index, restart = true) {
    if (!this.slides.length) return;
    this.activeIndex = (index + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (restart) {
      this.start();
    }
  }

  goToBlock(blockId) {
    const index = this.slides.findIndex((slide) => slide.dataset.slideBlockId === blockId);
    if (index >= 0) {
      this.goTo(index, false);
      this.stop();
    }
  }
}

const slideshowControllers = new WeakMap();

function initSlideshows(container = document) {
  container.querySelectorAll?.('[data-slideshow]').forEach((root) => {
    if (slideshowControllers.has(root)) return;
    slideshowControllers.set(root, new SlideshowController(root));
  });
}

function destroySlideshows(container = document) {
  container.querySelectorAll?.('[data-slideshow]').forEach((root) => {
    const controller = slideshowControllers.get(root);
    if (!controller) return;
    controller.destroy();
    slideshowControllers.delete(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initSlideshows(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initSlideshows(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroySlideshows(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest?.('[data-slide-block-id]');
  const slideshowRoot = event.target.closest?.('[data-slideshow]');
  if (!slide || !slideshowRoot) return;
  const controller = slideshowControllers.get(slideshowRoot);
  controller?.goToBlock(slide.dataset.slideBlockId);
});

class CategoryShowcaseController {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-category-track]');
    this.pages = [...root.querySelectorAll('[data-category-page]')];
    this.dots = [...root.querySelectorAll('[data-category-dot]')];
    this.autoplayEnabled = root.dataset.mobileAutoplay === 'true';
    this.loopEnabled = root.dataset.mobileLoop !== 'false';
    this.interval = Number(root.dataset.mobileInterval || 5000);
    this.activeIndex = 0;
    this.timer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;

    this.handleResize = this.handleResize.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);

    if (!this.track || this.pages.length < 2) {
      return;
    }

    this.attachEvents();
    this.goTo(0, false);
    this.start();
  }

  isMobile() {
    return window.matchMedia('(max-width: 47.99rem)').matches;
  }

  attachEvents() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
      });
    });
    this.root.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.root.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  destroy() {
    this.stop();
    this.root.removeEventListener('touchstart', this.handleTouchStart);
    this.root.removeEventListener('touchend', this.handleTouchEnd);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleResize() {
    if (!this.track) return;
    this.goTo(this.activeIndex, false);
    if (this.isMobile()) {
      this.start();
    } else {
      this.stop();
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stop();
    } else {
      this.start();
    }
  }

  handleTouchStart(event) {
    if (!this.isMobile()) return;
    this.touchStartX = event.changedTouches?.[0]?.clientX || 0;
  }

  handleTouchEnd(event) {
    if (!this.isMobile()) return;
    this.touchEndX = event.changedTouches?.[0]?.clientX || 0;
    const delta = this.touchEndX - this.touchStartX;
    if (Math.abs(delta) < 36) return;
    if (delta > 0) {
      this.goTo(this.activeIndex - 1);
    } else {
      this.goTo(this.activeIndex + 1);
    }
  }

  start() {
    if (!this.autoplayEnabled || !this.isMobile() || this.pages.length < 2) return;
    this.stop();
    this.timer = window.setInterval(() => {
      this.goTo(this.activeIndex + 1, false);
    }, this.interval);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  goTo(index, restart = true) {
    if (!this.track || !this.pages.length) return;

    let nextIndex = index;
    if (this.loopEnabled) {
      nextIndex = (index + this.pages.length) % this.pages.length;
    } else {
      nextIndex = Math.max(0, Math.min(index, this.pages.length - 1));
    }

    this.activeIndex = nextIndex;
    this.track.style.transform = `translate3d(-${this.activeIndex * 100}%, 0, 0)`;

    this.pages.forEach((page, pageIndex) => {
      page.classList.toggle('is-active', pageIndex === this.activeIndex);
    });

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (restart) {
      this.start();
    }
  }

  goToBlock(blockId) {
    const index = this.pages.findIndex((page) => {
      const ids = page.dataset.pageBlockIds || '';
      return ids.split(',').includes(blockId);
    });
    if (index >= 0) {
      this.goTo(index, false);
      this.stop();
    }
  }
}

const categoryShowcaseControllers = new WeakMap();

function initCategoryShowcases(container = document) {
  container.querySelectorAll?.('[data-category-showcase]').forEach((root) => {
    if (categoryShowcaseControllers.has(root)) return;
    categoryShowcaseControllers.set(root, new CategoryShowcaseController(root));
  });
}

function destroyCategoryShowcases(container = document) {
  container.querySelectorAll?.('[data-category-showcase]').forEach((root) => {
    const controller = categoryShowcaseControllers.get(root);
    if (!controller) return;
    controller.destroy();
    categoryShowcaseControllers.delete(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initCategoryShowcases(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initCategoryShowcases(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyCategoryShowcases(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  const blockNode = event.target.closest?.('[data-category-block-id]');
  const root = event.target.closest?.('[data-category-showcase]');
  if (!blockNode || !root) return;
  const controller = categoryShowcaseControllers.get(root);
  controller?.goToBlock(blockNode.dataset.categoryBlockId);
});

class CollectionShowcaseController {
  constructor(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-collection-showcase-viewport]');
    this.prevButton = root.querySelector('[data-collection-showcase-prev]');
    this.nextButton = root.querySelector('[data-collection-showcase-next]');
    this.pagination = root.querySelector('[data-collection-showcase-pagination]');
    this.dots = [];
    this.pageCount = 1;

    this.handlePrev = this.handlePrev.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);

    if (!this.viewport) return;

    this.attachEvents();
    this.rebuild();
  }

  attachEvents() {
    this.prevButton?.addEventListener('click', this.handlePrev);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
  }

  destroy() {
    this.prevButton?.removeEventListener('click', this.handlePrev);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.viewport?.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  }

getPageWidth() {
    return this.viewport?.clientWidth || 1;
  }

  getActivePage() {
    if (!this.viewport) return 0;
    // Using Math.round fixes the premature trigger of the next dot
    return Math.round(this.viewport.scrollLeft / this.getPageWidth());
  }

  rebuildDots() {
    if (!this.pagination) return;
    this.pagination.innerHTML = '';
    this.dots = [];

    if (this.pageCount <= 1) {
      this.pagination.hidden = true;
      return;
    }

    this.pagination.hidden = false;

    for (let index = 0; index < this.pageCount; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'collection-showcase__dot';
      dot.setAttribute('aria-label', `Go to products page ${index + 1}`);
      dot.addEventListener('click', () => {
        this.scrollToPage(index);
      });
      this.pagination.appendChild(dot);
      this.dots.push(dot);
    }
  }

  updateControls() {
    const activePage = this.getActivePage();

    this.prevButton && (this.prevButton.disabled = activePage <= 0);
    this.nextButton && (this.nextButton.disabled = activePage >= this.pageCount - 1);

    this.dots.forEach((dot, index) => {
      const isActive = index === activePage;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  rebuild() {
    if (!this.viewport) return;

    // Accurate page count: Total scrollable width divided by viewport width.
    // Math.round eliminates the "ghost" extra page caused by CSS gaps.
    this.pageCount = Math.round(this.viewport.scrollWidth / this.getPageWidth());

    // Failsafe: Ensure there is always at least 1 page recognized
    this.pageCount = Math.max(1, this.pageCount);

    this.rebuildDots();
    this.updateControls();
  }

  scrollToPage(index) {
    if (!this.viewport) return;
    const clampedIndex = Math.max(0, Math.min(index, this.pageCount - 1));

    // 1. Temporarily disable CSS scroll snapping to prevent the "twitch" fight
    this.viewport.style.scrollSnapType = 'none';

    // 2. Scroll exactly to the right edge if it's the last page, otherwise scroll normally
    if (clampedIndex === this.pageCount - 1) {
      this.viewport.scrollTo({
        left: this.viewport.scrollWidth,
        behavior: 'smooth'
      });
    } else {
      this.viewport.scrollTo({
        left: clampedIndex * this.getPageWidth(),
        behavior: 'smooth'
      });
    }

    // 3. Re-enable scroll snapping after the smooth scroll finishes (~600ms)
    clearTimeout(this.snapTimeout);
    this.snapTimeout = setTimeout(() => {
      this.viewport.style.scrollSnapType = '';
      
      // Minor manual snap to ensure it rests perfectly after turning CSS back on
      this.updateControls();
    }, 600);
  }

  handlePrev() {
    this.scrollToPage(this.getActivePage() - 1);
  }

  handleNext() {
    this.scrollToPage(this.getActivePage() + 1);
  }

  handleResize() {
    this.rebuild();
  }

  handleScroll() {
    this.updateControls();
  }
}

const collectionShowcaseControllers = new WeakMap();

function initCollectionShowcases(container = document) {
  container.querySelectorAll?.('[data-collection-showcase]').forEach((root) => {
    if (collectionShowcaseControllers.has(root)) return;
    collectionShowcaseControllers.set(root, new CollectionShowcaseController(root));
  });
}

function destroyCollectionShowcases(container = document) {
  container.querySelectorAll?.('[data-collection-showcase]').forEach((root) => {
    const controller = collectionShowcaseControllers.get(root);
    if (!controller) return;
    controller.destroy();
    collectionShowcaseControllers.delete(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initCollectionShowcases(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initCollectionShowcases(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyCollectionShowcases(event.target);
});

class CollectionPageController {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId || '';
    this.filtersOpenByDefault = root.dataset.filtersOpenDefault !== 'false';
    this.inlineCollapsed = !this.filtersOpenByDefault;
    this.isLoading = false;
    this.abortController = null;
    this.mobileQuery = window.matchMedia('(max-width: 47.99rem)');
    this.observer = null;
    this.headerWrapper = document.querySelector('.smart-sticky');

    this.handleClick = this.handleClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handlePopState = this.handlePopState.bind(this);

    this.cacheElements();
    this.attachEvents();
    this.root.dataset.collectionEnhanced = 'true';
    this.applyInlineState();
    this.initPriceRanges();
    this.refreshResponsiveState();
  }

  cacheElements() {
    this.dynamic = this.root.querySelector('[data-collection-dynamic]');
    this.results = this.root.querySelector('[data-collection-results]');
    this.grid = this.root.querySelector('[data-collection-grid]');
    this.filtersPanel = this.root.querySelector('[data-collection-filters-panel]');
    this.filtersDialog = this.root.querySelector('.collection-page__filters-dialog');
    this.paginationShell = this.root.querySelector('[data-collection-pagination-shell]');
    this.loadMoreLink = this.root.querySelector('[data-collection-load-more]');
    this.infiniteSentinel = this.root.querySelector('[data-collection-infinite-sentinel]');
    this.filterToggleButtons = [...this.root.querySelectorAll('[data-collection-filter-toggle]')];
  }

  attachEvents() {
    this.root.addEventListener('click', this.handleClick);
    this.root.addEventListener('submit', this.handleSubmit);
    this.root.addEventListener('change', this.handleChange);
    this.root.addEventListener('input', this.handleInput);
    this.root.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('popstate', this.handlePopState);
  }

  destroy() {
    this.disconnectObserver();
    this.abortController?.abort();
    this.root.removeEventListener('click', this.handleClick);
    this.root.removeEventListener('submit', this.handleSubmit);
    this.root.removeEventListener('change', this.handleChange);
    this.root.removeEventListener('input', this.handleInput);
    this.root.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('popstate', this.handlePopState);
    this.unlockPageScroll();
  }

  isMobile() {
    return this.mobileQuery.matches;
  }

  usesDrawerMode() {
    return this.isMobile()
      ? this.root.dataset.mobileFilterLayout === 'drawer'
      : this.root.dataset.desktopFilterLayout === 'drawer';
  }

  getPaginationMode() {
    return this.isMobile() ? this.root.dataset.paginationModeMobile : this.root.dataset.paginationModeDesktop;
  }

  applyInlineState() {
    if (this.usesDrawerMode()) {
      this.root.classList.remove('is-filters-collapsed');
      this.closeFilters({ restoreFocus: false });
      return;
    }

    this.root.classList.toggle('is-filters-collapsed', this.inlineCollapsed);
    this.root.classList.remove('is-filters-open');
    this.unlockPageScroll();
    this.updateFilterToggle();
  }

  updateFilterToggle() {
    const isExpanded = this.usesDrawerMode()
      ? this.root.classList.contains('is-filters-open')
      : !this.root.classList.contains('is-filters-collapsed');

    this.filterToggleButtons.forEach((button) => {
      button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      button.classList.toggle('is-active', isExpanded);
    });

    if (this.filtersPanel) {
      this.filtersPanel.setAttribute('aria-hidden', isExpanded ? 'false' : 'true');
    }
  }

  lockPageScroll() {
    AyuTheme.lockScroll(true);
  }

  unlockPageScroll() {
    if (document.querySelector('.drawer.is-open')) return;
    if ([...document.querySelectorAll('[data-collection-page].is-filters-open')].some((node) => node !== this.root)) {
      return;
    }
    AyuTheme.lockScroll(false);
  }

  openFilters() {
    if (!this.filtersPanel) return;

    if (this.usesDrawerMode()) {
      this.root.classList.add('is-filters-open');
      this.lockPageScroll();
      this.updateFilterToggle();

      const focusTarget = this.filtersDialog?.querySelector(
        '[data-collection-focus-target], input, button, select, textarea, a[href]'
      );
      if (focusTarget) {
        window.requestAnimationFrame(() => {
          focusTarget.focus({ preventScroll: true });
        });
      }
      return;
    }

    this.inlineCollapsed = false;
    this.root.classList.remove('is-filters-collapsed');
    this.updateFilterToggle();
  }

  closeFilters({ restoreFocus = true } = {}) {
    this.root.classList.remove('is-filters-open');
    this.unlockPageScroll();
    this.updateFilterToggle();

    if (restoreFocus && this.filterToggleButtons[0]) {
      this.filterToggleButtons[0].focus({ preventScroll: true });
    }
  }

  toggleFilters() {
    if (!this.filtersPanel) return;

    if (this.usesDrawerMode()) {
      if (this.root.classList.contains('is-filters-open')) {
        this.closeFilters();
      } else {
        this.openFilters();
      }
      return;
    }

    this.inlineCollapsed = !this.inlineCollapsed;
    this.root.classList.toggle('is-filters-collapsed', this.inlineCollapsed);
    this.updateFilterToggle();
  }

  refreshResponsiveState() {
    this.root.dataset.activePaginationMode = this.getPaginationMode();
    this.applyInlineState();
    this.updateStickyOffsets();
    this.setupInfiniteScroll();
  }

  handleResize() {
    this.refreshResponsiveState();
  }

  handleScroll() {
    this.updateStickyOffsets();
  }

  handleKeyDown(event) {
    if (event.key === 'Escape' && this.root.classList.contains('is-filters-open')) {
      this.closeFilters();
    }
  }

  handlePopState() {
    if (!document.body.contains(this.root)) return;
    this.loadCollection(window.location.href, { replaceHistory: false, scrollToResults: false });
  }

  handleClick(event) {
    const toggleButton = event.target.closest('[data-collection-filter-toggle]');
    if (toggleButton) {
      event.preventDefault();
      this.toggleFilters();
      return;
    }

    const closeButton = event.target.closest('[data-collection-filter-close]');
    if (closeButton) {
      event.preventDefault();
      if (this.usesDrawerMode()) {
        this.closeFilters();
      } else {
        this.inlineCollapsed = true;
        this.root.classList.add('is-filters-collapsed');
        this.updateFilterToggle();
        this.filterToggleButtons[0]?.focus({ preventScroll: true });
      }
      return;
    }

    const loadMoreLink = event.target.closest('[data-collection-load-more]');
    if (loadMoreLink) {
      event.preventDefault();
      if (!this.isLoading) {
        this.loadCollection(this.normalizeCollectionUrl(loadMoreLink.href), {
          append: true,
          replaceHistory: false,
          scrollToResults: false,
        });
      }
      return;
    }

    const collectionLink = event.target.closest('[data-collection-page-link], [data-collection-link]');
    if (collectionLink) {
      event.preventDefault();
      this.loadCollection(this.normalizeCollectionUrl(collectionLink.href), {
        replaceHistory: true,
        scrollToResults: true,
      });
    }
  }

  handleSubmit(event) {
    const form = event.target.closest('[data-collection-filters-form], [data-collection-sort-form]');
    if (!form) return;

    event.preventDefault();
    const url = this.buildUrlFromForm(form);
    const isSortForm = form.matches('[data-collection-sort-form]');

    AyuTheme.emitAnalytics(isSortForm ? 'collection_sort_submit' : 'collection_filter_submit', {
      url,
      context: 'collection_page',
    });

    this.loadCollection(url, { replaceHistory: true, scrollToResults: true });
    if (this.usesDrawerMode()) {
      this.closeFilters({ restoreFocus: false });
    }
  }

  handleChange(event) {
    const filterForm = event.target.closest('[data-collection-filters-form]');
    if (!filterForm) return;
    if (this.root.dataset.autoSubmit !== 'true') return;
    if (event.target.matches('[data-price-input], [data-price-range-input], [data-filter-search-input]')) return;

    const input = event.target.closest('input, select');
    if (!input) return;
    filterForm.requestSubmit();
  }

  handleInput(event) {
    const priceRange = event.target.closest('[data-price-range]');
    if (priceRange && event.target.matches('[data-price-input], [data-price-range-input]')) {
      this.syncPriceRange(priceRange, event.target.dataset.priceInput || event.target.dataset.priceRangeInput || '');
      return;
    }

    const searchInput = event.target.closest('[data-filter-search-input]');
    if (searchInput) {
      this.filterOptionList(searchInput);
    }
  }

  buildUrlFromForm(form) {
    const target = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
    const url = new URL(window.location.href);
    const formData = new FormData(form);

    url.pathname = target.pathname;
    this.clearCollectionStateParams(url.searchParams);

    for (const [key, value] of formData.entries()) {
      if (value === '' || value == null) continue;
      url.searchParams.append(key, value);
    }

    return url.toString();
  }

  clearCollectionStateParams(searchParams) {
    [...new Set([...searchParams.keys()])].forEach((key) => {
      if (key === 'page' || key === 'sort_by' || key.startsWith('filter.')) {
        searchParams.delete(key);
      }
    });
  }

  normalizeCollectionUrl(url) {
    const incoming = new URL(url, window.location.origin);
    const normalized = new URL(window.location.href);

    normalized.pathname = incoming.pathname;
    normalized.hash = incoming.hash;
    this.clearCollectionStateParams(normalized.searchParams);

    [...new Set([...incoming.searchParams.keys()])].forEach((key) => {
      normalized.searchParams.delete(key);
    });

    incoming.searchParams.forEach((value, key) => {
      normalized.searchParams.append(key, value);
    });

    return normalized.toString();
  }

  filterOptionList(searchInput) {
    const query = searchInput.value.trim().toLowerCase();
    const group = searchInput.closest('[data-filter-group]');
    if (!group) return;

    group.querySelectorAll('[data-filter-option-item]').forEach((item) => {
      const label = item.querySelector('[data-filter-option-label]')?.textContent?.trim().toLowerCase() || '';
      item.hidden = query !== '' && !label.includes(query);
    });
  }

  initPriceRanges() {
    this.root.querySelectorAll('[data-price-range]').forEach((range) => {
      this.syncPriceRange(range);
    });
  }

  syncPriceRange(rangeRoot, changedControl = '') {
    const max = Number(rangeRoot.dataset.rangeMax || 0) || 0;
    const minInput = rangeRoot.querySelector('[data-price-input="min"]');
    const maxInput = rangeRoot.querySelector('[data-price-input="max"]');
    const minRange = rangeRoot.querySelector('[data-price-range-input="min"]');
    const maxRange = rangeRoot.querySelector('[data-price-range-input="max"]');
    const minLabel = rangeRoot.querySelector('[data-price-display-min]');
    const maxLabel = rangeRoot.querySelector('[data-price-display-max]');

    if (!minInput || !maxInput || !minRange || !maxRange) return;

    let minValue = Number(minInput.value || minRange.value || 0);
    let maxValue = Number(maxInput.value || maxRange.value || max);

    if (changedControl === 'min') {
      minValue = Math.min(minValue, maxValue);
    }

    if (changedControl === 'max') {
      maxValue = Math.max(maxValue, minValue);
    }

    minValue = Math.max(0, Math.min(minValue, max));
    maxValue = Math.max(minValue, Math.min(maxValue, max));

    const preserveBlankMin = changedControl === '' && minInput.value === '' && minValue === 0;
    const preserveBlankMax = changedControl === '' && maxInput.value === '' && maxValue === max;

    minInput.value = preserveBlankMin ? '' : minValue;
    maxInput.value = preserveBlankMax ? '' : maxValue;
    minRange.value = minValue;
    maxRange.value = maxValue;

    const minPercent = max > 0 ? (minValue / max) * 100 : 0;
    const maxPercent = max > 0 ? (maxValue / max) * 100 : 100;
    rangeRoot.style.setProperty('--price-min-percent', `${minPercent}%`);
    rangeRoot.style.setProperty('--price-max-percent', `${maxPercent}%`);

    if (minLabel) minLabel.textContent = AyuTheme.formatMoney(minValue * 100);
    if (maxLabel) maxLabel.textContent = AyuTheme.formatMoney(maxValue * 100);

    if (minValue >= max - 1) {
      minRange.style.zIndex = '4';
      maxRange.style.zIndex = '3';
    } else {
      minRange.style.zIndex = '2';
      maxRange.style.zIndex = '3';
    }
  }

  updateStickyOffsets() {
    const headerBottom = this.headerWrapper ? this.headerWrapper.getBoundingClientRect().bottom : 0;
    const stickyTop = Math.max(12, Math.round(Math.max(0, headerBottom) + 8));
    this.root.style.setProperty('--collection-sticky-top', `${stickyTop}px`);
    this.root.style.setProperty('--collection-results-scroll-margin', `${stickyTop + 48}px`);
  }

  disconnectObserver() {
    if (!this.observer) return;
    this.observer.disconnect();
    this.observer = null;
  }

  setupInfiniteScroll() {
    this.disconnectObserver();

    if (this.getPaginationMode() !== 'infinite') return;
    if (!this.infiniteSentinel || !this.loadMoreLink) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || this.isLoading) return;
          this.loadCollection(this.normalizeCollectionUrl(this.loadMoreLink.href), {
            append: true,
            replaceHistory: false,
            scrollToResults: false,
          });
        });
      },
      {
        rootMargin: '280px 0px 280px 0px',
      }
    );

    this.observer.observe(this.infiniteSentinel);
  }

  setLoadingState(isLoading) {
    this.isLoading = isLoading;
    this.root.classList.toggle('is-loading', isLoading);
    this.root.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  replaceDynamicContent(nextDynamic) {
    if (!this.dynamic || !nextDynamic) return;

    AyuTheme.destroyProductCards?.(this.dynamic);
    this.dynamic.innerHTML = nextDynamic.innerHTML;
    this.cacheElements();
    AyuTheme.initProductCards?.(this.dynamic);
    this.initPriceRanges();
    this.refreshResponsiveState();
  }

  appendResults(nextRoot) {
    const nextGrid = nextRoot.querySelector('[data-collection-grid]');
    const nextPaginationShell = nextRoot.querySelector('[data-collection-pagination-shell]');

    if (!this.grid || !nextGrid) return;

    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = nextGrid.innerHTML;
    while (tempContainer.firstChild) {
      this.grid.appendChild(tempContainer.firstChild);
    }

    AyuTheme.initProductCards?.(this.grid);

    if (this.paginationShell && nextPaginationShell) {
      this.paginationShell.innerHTML = nextPaginationShell.innerHTML;
    }

    this.cacheElements();
    this.refreshResponsiveState();
  }

  async loadCollection(url, { append = false, replaceHistory = false, scrollToResults = true } = {}) {
    if (!url) return;
    const normalizedUrl = this.normalizeCollectionUrl(url);
    if (this.isLoading) {
      this.abortController?.abort();
    }

    this.abortController = new AbortController();
    this.setLoadingState(true);

    try {
      const response = await fetch(normalizedUrl, {
        signal: this.abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextRoot =
        parsedDocument.querySelector(`[data-collection-page][data-section-id="${this.sectionId}"]`) ||
        parsedDocument.querySelector('[data-collection-page]');
      const nextDynamic = nextRoot?.querySelector('[data-collection-dynamic]');

      if (!nextRoot || !nextDynamic) {
        throw new Error('Collection markup not found in response.');
      }

      if (append) {
        this.appendResults(nextRoot);
        AyuTheme.emitAnalytics('collection_load_more', {
          page_url: normalizedUrl,
          context: 'collection_page',
        });
      } else {
        this.replaceDynamicContent(nextDynamic);
        if (replaceHistory) {
          window.history.pushState({ collectionUrl: normalizedUrl }, '', normalizedUrl);
        }
        if (scrollToResults) {
          this.results?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        AyuTheme.emitAnalytics('collection_refresh', {
          page_url: normalizedUrl,
          context: 'collection_page',
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(normalizedUrl);
    } finally {
      this.abortController = null;
      this.setLoadingState(false);
    }
  }
}

const collectionPageControllers = new WeakMap();

function initCollectionPages(container = document) {
  container.querySelectorAll?.('[data-collection-page]').forEach((root) => {
    if (collectionPageControllers.has(root)) return;
    collectionPageControllers.set(root, new CollectionPageController(root));
  });
}

function destroyCollectionPages(container = document) {
  container.querySelectorAll?.('[data-collection-page]').forEach((root) => {
    const controller = collectionPageControllers.get(root);
    if (!controller) return;
    controller.destroy();
    collectionPageControllers.delete(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initCollectionPages(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initCollectionPages(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyCollectionPages(event.target);
});

// Mega menu trigger - header

const megaMenuControllers = new WeakMap();

class MegaMenuController {
  constructor(root) {
    this.root = root;
    this.trigger = root.querySelector('[data-mega-trigger]');
    this.panel = root.querySelector('[data-mega-panel]');
    this.accordionGroup = root.querySelector('[data-mega-accordion-group]');
    this.navList = root.querySelector('.am-nav-list');
    this.navScroll = root.querySelector('.am-nav-scroll');
    this.closeTimer = null;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.sliderControllers = Array.from(root.querySelectorAll('[data-mega-slider]'))
      .map((slider) => this.createSliderController(slider))
      .filter(Boolean);

    this.handlePointerEnter = this.open.bind(this);
    this.handlePointerLeave = this.close.bind(this);
    this.handleFocusIn = this.onFocusIn.bind(this);
    this.handleFocusOut = this.onFocusOut.bind(this);
    this.handleKeyDown = this.onKeyDown.bind(this);
    this.handleClick = this.onClick.bind(this);
    this.handleNavScroll = this.updateNavOverflowState.bind(this);
    this.handleResize = this.updateNavOverflowState.bind(this);

    this.bindEvents();
    this.updateNavOverflowState();
  }

  bindEvents() {
    this.root.addEventListener('mouseenter', this.handlePointerEnter);
    this.root.addEventListener('mouseleave', this.handlePointerLeave);
    this.root.addEventListener('focusin', this.handleFocusIn);
    this.root.addEventListener('focusout', this.handleFocusOut);
    this.root.addEventListener('keydown', this.handleKeyDown);
    this.root.addEventListener('click', this.handleClick);
    this.navList?.addEventListener('scroll', this.handleNavScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });
  }

  createSliderController(slider) {
    const slides = Array.from(slider.querySelectorAll('[data-mega-slide]'));
    const dots = Array.from(slider.querySelectorAll('[data-mega-dot]'));

    if (slides.length <= 1) return null;

    let currentIndex = 0;
    let intervalId = null;
    const delay = Number.parseInt(slider.dataset.autoplay || '4000', 10);

    const showSlide = (index) => {
      currentIndex = index;
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.classList.toggle('is-active', isActive);
        slide.hidden = !isActive;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.tabIndex = isActive ? 0 : -1;
      });

      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    };

    const stop = () => {
      if (intervalId) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    const start = () => {
      if (this.prefersReducedMotion.matches) return;
      stop();
      intervalId = window.setInterval(() => {
        showSlide((currentIndex + 1) % slides.length);
      }, delay);
    };

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        showSlide(index);
        stop();
        if (this.root.classList.contains('is-open')) start();
      });
    });

    slider.addEventListener('mouseenter', stop);
    slider.addEventListener('mouseleave', () => {
      if (this.root.classList.contains('is-open')) start();
    });
    slider.addEventListener('focusin', stop);
    slider.addEventListener('focusout', (event) => {
      if (!slider.contains(event.relatedTarget) && this.root.classList.contains('is-open')) {
        start();
      }
    });

    showSlide(0);

    return { start, stop };
  }

  setAccordionState(item, expanded) {
    if (!item) return;

    item.classList.toggle('is-open', expanded);

    const toggle = item.querySelector('[data-mega-accordion-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    const content = item.querySelector('.am-nav-children');
    if (content) {
      content.hidden = !expanded;
    }

    window.requestAnimationFrame(() => this.updateNavOverflowState());
  }

  updateNavOverflowState() {
    if (!this.navList || !this.navScroll) return;

    const overflowAmount = this.navList.scrollHeight - this.navList.clientHeight;
    const hasOverflow = overflowAmount > 8;
    const atEnd = this.navList.scrollTop + this.navList.clientHeight >= this.navList.scrollHeight - 6;

    this.navScroll.classList.toggle('has-overflow', hasOverflow);
    this.navScroll.classList.toggle('can-scroll-more', hasOverflow && !atEnd);
  }

  open() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    const wasOpen = this.root.classList.contains('is-open');
    this.root.classList.add('is-open');
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
    if (this.panel) this.panel.setAttribute('aria-hidden', 'false');
    if (!wasOpen) {
      this.root.querySelector('.am-nav-list')?.scrollTo({ top: 0, behavior: 'auto' });
    }
    this.updateNavOverflowState();
    this.sliderControllers.forEach((controller) => controller.start());
  }

  close() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
    }

    this.closeTimer = window.setTimeout(() => {
      this.root.classList.remove('is-open');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      if (this.panel) this.panel.setAttribute('aria-hidden', 'true');
      this.sliderControllers.forEach((controller) => controller.stop());
      this.closeTimer = null;
    }, 110);
  }

  onFocusIn(event) {
    this.open();
  }

  onFocusOut(event) {
    if (this.root.contains(event.relatedTarget)) return;
    this.close();
  }

  onClick(event) {
    const accordionToggle = event.target.closest('[data-mega-accordion-toggle]');
    if (accordionToggle && this.root.contains(accordionToggle)) {
      event.preventDefault();

      const item = accordionToggle.closest('[data-mega-accordion-item]');
      if (!item) return;

      const shouldOpen = !item.classList.contains('is-open');
      const singleOpen = this.accordionGroup?.dataset.singleOpen === 'true';

      if (shouldOpen && singleOpen) {
        this.accordionGroup
          ?.querySelectorAll('[data-mega-accordion-item].is-open')
          .forEach((openItem) => {
            if (openItem !== item) this.setAccordionState(openItem, false);
          });
      }

      this.setAccordionState(item, shouldOpen);
      return;
    }

    const collapseToggle = event.target.closest('[data-collapse-toggle]');
    if (collapseToggle && this.root.contains(collapseToggle)) {
      event.preventDefault();
      const collapseRoot =
        collapseToggle.closest('.am-nav-children')?.querySelector('[data-collapse-root]') ||
        collapseToggle.previousElementSibling;
      if (!collapseRoot) return;

      const expanded = collapseRoot.classList.toggle('is-expanded');
      collapseToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      const labelNode = collapseToggle.querySelector('span');
      if (labelNode) {
        labelNode.textContent = expanded
          ? collapseToggle.dataset.collapseLabel || 'Show less'
          : collapseToggle.dataset.expandLabel || 'View more';
      }
      window.requestAnimationFrame(() => this.updateNavOverflowState());
    }
  }

  onKeyDown(event) {
    if (event.key !== 'Escape') return;
    this.close();
    this.trigger?.focus();
  }

  destroy() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    this.root.classList.remove('is-open');
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
    if (this.panel) this.panel.setAttribute('aria-hidden', 'true');
    this.sliderControllers.forEach((controller) => controller.stop());
    this.root.removeEventListener('mouseenter', this.handlePointerEnter);
    this.root.removeEventListener('mouseleave', this.handlePointerLeave);
    this.root.removeEventListener('focusin', this.handleFocusIn);
    this.root.removeEventListener('focusout', this.handleFocusOut);
    this.root.removeEventListener('keydown', this.handleKeyDown);
    this.root.removeEventListener('click', this.handleClick);
    this.navList?.removeEventListener('scroll', this.handleNavScroll);
    window.removeEventListener('resize', this.handleResize);
  }
}

function initMegaMenus(container = document) {
  container.querySelectorAll?.('[data-mega-root]').forEach((root) => {
    if (megaMenuControllers.has(root)) return;
    megaMenuControllers.set(root, new MegaMenuController(root));
  });
}

function destroyMegaMenus(container = document) {
  container.querySelectorAll?.('[data-mega-root]').forEach((root) => {
    const controller = megaMenuControllers.get(root);
    if (!controller) return;
    controller.destroy();
    megaMenuControllers.delete(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initMegaMenus(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initMegaMenus(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyMegaMenus(event.target);
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-mega-atc-form]');
  if (!form) return;

  event.preventDefault();

  const button = form.querySelector('.am-atc-button');
  const feedback = form.closest('.am-product-body')?.querySelector('[data-mega-atc-feedback]');
  const variantId = form.querySelector('[name="id"]')?.value;
  const defaultLabel = button?.dataset.defaultLabel || 'Add to cart';
  const addedLabel = button?.dataset.addedLabel || 'Added';

  if (!button || !variantId) return;

  button.disabled = true;
  button.textContent = 'Adding…';
  if (feedback) {
    feedback.textContent = '';
    delete feedback.dataset.tone;
  }

  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
      }),
    });

    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload.description || 'Unable to add this item right now.');
    }

    AyuTheme.incrementCartCount(1);
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => AyuTheme.refreshCartCount());
    } else {
      window.setTimeout(() => AyuTheme.refreshCartCount(), 250);
    }

    AyuTheme.emitAnalytics('add_to_cart', {
      variant_id: variantId,
      source: 'mega_menu',
    });

    button.textContent = addedLabel;
    if (feedback) {
      feedback.textContent = 'Added to cart';
      feedback.dataset.tone = 'success';
    }

    window.setTimeout(() => {
      button.textContent = defaultLabel;
      button.disabled = false;
    }, 1200);
  } catch (error) {
    button.textContent = defaultLabel;
    button.disabled = false;
    if (feedback) {
      feedback.textContent = error.message || 'Unable to add this item right now.';
      feedback.dataset.tone = 'error';
    }
  }
});
